/**
 * The 3PL's bulk "Replace blank" from the /products floating bar (Phase 92,
 * UI-SPEC 3.3; D-11, D-12, D-21, D-23).
 *
 * The selected shared blanks move, for NEW orders only, onto the blanks of
 * one other garment of the 3PL's own, size by size and colour by colour:
 *
 * 1. "Choose replacement": the garment picker (`SharedSkuPicker
 *    mode="garment"`, the caller's own shared garments; a garment whose every
 *    blank is in the selection is left out), then the SAME value-match table
 *    as the client composer (`BlankValueMatchTable`, no design column, the
 *    last option "Keep the current blank"). Rows are the selected blanks'
 *    colours and sizes, auto-matched by name or code, never free text.
 * 2. "Review": the 92-31 review block over the pairs the matches build. A
 *    kept row, or a size and colour the garment does not have, sends no pair.
 *
 * Reads (admins only, the dialog never renders for anyone else): one
 * `listOwnerSharedGarments` page per picker search (only once the picker is
 * opened), one `getOwnSharedGarmentBlanks` for the chosen garment, then one
 * `previewReplaceBlank` per change of the pairs (300 ms debounce, last answer
 * kept). Write: one `replaceBlank` per click, idempotent on retry, one toast
 * per click (`useReplaceBlank`). No polling.
 */
import { useMemo, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { NoteLine } from '@/components/shared/NoteLine';
import { SectionError } from '@/components/shared/SectionError';
import { useOrganization } from '@/contexts/OrganizationContext';
import { trpc } from '@/lib/trpc';
import {
  SharedSkuPicker,
  type SharedGarmentOption,
  type UseGarmentOptions,
} from '@/features/product-sync/components/shared-stock/SharedSkuPicker';
import {
  BlankValueMatchTable,
  type BlankValueGroup,
} from '@/features/product-sync/components/supply/BlankValueMatchTable';
import {
  reviewAllowsReplace,
  useOwnSharedGarmentBlanks,
  usePreviewReplaceBlank,
  useReplaceBlank,
} from '../../hooks/useSharedSkus';
import {
  blankSegments,
  buildMatchRows,
  buildReplacePlan,
  garmentValues,
  matchChoiceKey,
  REPLACE_MAX_PAIRS,
  type Choice,
  type ReplacePlan,
  type SharedBlankTarget,
} from '../../utils/replaceBlankPlan';
import { ReplaceReview } from './ReplaceReview';

const GARMENT_PAGE_LIMIT = 50;
/** Blanks named in the "no replacement in that size and colour" line before the rest are counted. */
const GAPS_NAMED_MAX = 5;

export type { SharedBlankTarget } from '../../utils/replaceBlankPlan';

export interface ReplaceBlankDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The selected blanks the caller shares (the bar passes only those). */
  selectedSkus: SharedBlankTarget[];
  /** After a replace (or an already-applied retry), once per click: refresh, clear. */
  onReplaced?: () => void;
}

export function ReplaceBlankDialog({ open, onOpenChange, selectedSkus, onReplaced }: ReplaceBlankDialogProps) {
  const { t } = useTranslation('inventory');
  const { organization } = useOrganization();
  const ownerId = organization?.id ?? '';
  const ownerName = organization?.name ?? '';
  const pickerId = 'replace-blank-garment';

  // The selection is snapshot at open: a refetch behind the dialog never
  // rewrites the rows the operator is matching.
  const [targets] = useState(() => selectedSkus);
  // The bar button that opened it. With no DialogTrigger, Radix would send
  // focus to the body on close (UI 25), so it is returned here.
  const [returnFocus] = useState(() =>
    typeof document === 'undefined' ? null : (document.activeElement as HTMLElement | null),
  );
  const [garment, setGarment] = useState<SharedGarmentOption | null>(null);
  const [choices, setChoices] = useState<Map<string, Choice>>(() => new Map());
  const [notice, setNotice] = useState<'maybe_finishing' | 'too_many_rows' | null>(null);

  const useGarmentOptions = useExcludingGarmentOptions(targets);
  const garmentBlanks = useOwnSharedGarmentBlanks(garment?.garmentKey ?? null);
  const blanks = useMemo(() => garmentBlanks.data?.blanks ?? [], [garmentBlanks.data]);
  const values = useMemo(() => garmentValues(blanks), [blanks]);
  const useSizes = values.sizes.length > 0;
  const colourRows = useMemo(() => buildMatchRows(targets, 'colour', values.colours, choices), [targets, values, choices]);
  const sizeRows = useMemo(
    () => (useSizes ? buildMatchRows(targets, 'size', values.sizes, choices) : []),
    [targets, values, choices, useSizes],
  );
  const tooMany = targets.length > REPLACE_MAX_PAIRS;
  const plan = useMemo<ReplacePlan | null>(
    () => (garment && blanks.length > 0 && !tooMany ? buildReplacePlan(targets, blanks, colourRows, sizeRows, useSizes) : null),
    [garment, blanks, tooMany, targets, colourRows, sizeRows, useSizes],
  );
  const pairs = plan?.kind === 'ready' && plan.pairs.length > 0 ? plan.pairs : null;
  const review = usePreviewReplaceBlank(pairs);
  const { replace, pending } = useReplaceBlank();

  const canSubmit = Boolean(pairs) && reviewAllowsReplace(review) && !pending && notice !== 'too_many_rows';

  const submit = () => {
    if (!canSubmit || !pairs || !garment) return;
    replace(pairs, garment.title, (outcome) => {
      switch (outcome.kind) {
        case 'replaced':
        case 'already_applied':
          onReplaced?.();
          onOpenChange(false);
          return;
        case 'maybe_finishing':
          setNotice('maybe_finishing');
          review.retry();
          return;
        case 'too_many_rows':
          setNotice('too_many_rows');
          return;
        case 'refused':
          review.retry();
          return;
      }
    });
  };

  const onMatch = (group: BlankValueGroup, value: string | null, blankValue: string | null) => {
    setNotice(null);
    setChoices((current) => new Map(current).set(matchChoiceKey(group, value), { blankValue }));
  };

  const gaps = plan?.kind === 'ready' ? plan.gaps : [];
  const gapList = [
    gaps.slice(0, GAPS_NAMED_MAX).map((g) => g.sku).join(', '),
    gaps.length > GAPS_NAMED_MAX ? `+${gaps.length - GAPS_NAMED_MAX}` : '',
  ]
    .filter(Boolean)
    .join(' ');

  let matchBody = null;
  if (garment) {
    if (garmentBlanks.isError) {
      matchBody = (
        <SectionError
          variant="compact"
          body={t('sharedSkus.replace.blanksError')}
          onRetry={() => void garmentBlanks.refetch()}
          retryLabel={t('sharedSkus.replace.blanksRetry')}
          retrying={garmentBlanks.isFetching}
        />
      );
    } else if (!garmentBlanks.data) {
      matchBody = (
        <div className="space-y-2" aria-hidden="true" data-testid="replace-blank-match-skeleton">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      );
    } else {
      matchBody = (
        <BlankValueMatchTable
          idPrefix="replace-blank-match"
          matches={{ colours: colourRows, sizes: sizeRows }}
          blankValues={values}
          designFor={() => false}
          ownerName={ownerName}
          onMatch={onMatch}
          onDesignChoice={() => {}}
          leaveOutLabel={t('sharedSkus.bulk.keepCurrent')}
          showDesignChoice={false}
          fromColumnLabel={t('sharedSkus.bulk.colCurrent')}
          leftOutStateLabel={t('sharedSkus.bulk.kept')}
        />
      );
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && pending) return;
        onOpenChange(next);
      }}
    >
      <DialogContent
        className="max-h-[85vh] overflow-y-auto sm:max-w-3xl"
        onOpenAutoFocus={(event) => {
          // Focus lands on the garment picker, the first thing to do.
          event.preventDefault();
          document.getElementById(pickerId)?.focus();
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (returnFocus?.isConnected) returnFocus.focus();
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' || !(event.metaKey || event.ctrlKey) || event.nativeEvent.isComposing) return;
          event.preventDefault();
          submit();
        }}
      >
        <DialogHeader>
          <DialogTitle>{t('sharedSkus.bulk.replaceTitle')}</DialogTitle>
          <DialogDescription>{t('sharedSkus.replace.subline')}</DialogDescription>
        </DialogHeader>

        <div className="grid min-w-0 gap-6">
          <section className="min-w-0 space-y-3" aria-labelledby="replace-blank-choose">
            <h3 id="replace-blank-choose" className="text-sm font-medium">
              {t('sharedSkus.bulk.sectionChoose')}
            </h3>
            {tooMany ? (
              <NoteLine tone="warning" icon={AlertTriangle} className="leading-normal">
                {t('sharedSkus.bulk.tooManyBlanks', { max: REPLACE_MAX_PAIRS })}
              </NoteLine>
            ) : (
              <>
                <SharedSkuPicker
                  mode="garment"
                  id={pickerId}
                  ownerOrganizationId={ownerId}
                  ownerName={ownerName}
                  label={t('sharedSkus.replace.pickerLabel')}
                  value={garment}
                  onGarmentChange={(next) => {
                    setGarment(next);
                    setChoices(new Map());
                    setNotice(null);
                  }}
                  useGarmentOptions={useGarmentOptions}
                />
                {matchBody && <div className="min-w-0 motion-safe:animate-fade-in">{matchBody}</div>}
                {plan?.kind === 'incomplete' && (
                  <p className="text-xs leading-normal text-amber-700">{t('sharedSkus.bulk.matchHelper')}</p>
                )}
                {gaps.length > 0 && (
                  <NoteLine tone="warning" icon={AlertTriangle} className="leading-normal [overflow-wrap:anywhere]">
                    {t('sharedSkus.bulk.gaps', { count: gaps.length, list: gapList })}
                  </NoteLine>
                )}
                {plan?.kind === 'ready' && plan.pairs.length === 0 && gaps.length === 0 && (
                  <NoteLine className="leading-normal">{t('sharedSkus.bulk.allKept')}</NoteLine>
                )}
              </>
            )}
          </section>

          {pairs && (
            <section className="min-w-0 space-y-3 motion-safe:animate-fade-in" aria-labelledby="replace-blank-review">
              <h3 id="replace-blank-review" className="text-sm font-medium">
                {t('sharedSkus.replace.reviewLabel')}
              </h3>
              <ReplaceReview
                review={review}
                onRetry={review.retry}
                showSubline={false}
                noRowsText={t('sharedSkus.bulk.noRows')}
              />
              {notice && (
                <div role="alert">
                  <NoteLine tone="warning" icon={AlertTriangle} className="leading-normal">
                    {notice === 'maybe_finishing'
                      ? t('sharedSkus.replace.maybeFinishing')
                      : t('sharedSkus.replace.tooManyRows')}
                  </NoteLine>
                </div>
              )}
            </section>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" disabled={pending} onClick={() => onOpenChange(false)}>
            {t('sharedSkus.bulk.closeReplace')}
          </Button>
          <Button
            type="button"
            className="gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
            disabled={!canSubmit}
            onClick={submit}
          >
            {pending && <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
            {t('sharedSkus.replace.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The caller's own shared garments, minus any garment whose every blank is in
 * the selection (nothing left to move onto). Same single read as the
 * picker's default source. Built once from the snapshot selection, so the
 * hook identity is fixed for the dialog's life, as the picker requires.
 */
function useExcludingGarmentOptions(targets: SharedBlankTarget[]): UseGarmentOptions {
  const [hook] = useState<UseGarmentOptions>(() => {
    const selectedPerKey = new Map<string, number>();
    for (const target of targets) {
      const key = blankSegments(target.sku).key.toUpperCase();
      selectedPerKey.set(key, (selectedPerKey.get(key) ?? 0) + 1);
    }
    const useOwnGarmentsExcludingSelected: UseGarmentOptions = ({ ownerOrganizationId, search, enabled }) => {
      const query = trpc.inventory.listOwnerSharedGarments.useQuery(
        { ownerOrganizationId, search: search || undefined, limit: GARMENT_PAGE_LIMIT },
        { enabled: enabled && Boolean(ownerOrganizationId), placeholderData: keepPreviousData, staleTime: 30_000, retry: 1 },
      );
      const page = query.data?.items ?? [];
      // filter-layer-ok: drops only the known garments whose every blank is selected; the total drops by the same number.
      const items = page.filter((item) => (selectedPerKey.get(item.garmentKey.toUpperCase()) ?? 0) < item.blankCount);
      const refetch = query.refetch;
      return {
        items,
        total: Math.max(0, (query.data?.total ?? 0) - (page.length - items.length)),
        blanksAllowed: query.data?.blanksAllowed ?? true,
        isLoading: query.isLoading,
        isError: query.isError,
        refetch: () => void refetch(),
      };
    };
    return useOwnGarmentsExcludingSelected;
  });
  return hook;
}
